import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Mail, Plus, Send, Trash2, Pause, Play, CalendarCheck, Clock } from "lucide-react";

const SECTIONS = [
  { key: "executive_summary", label: "Executive summary (tracked/top10/improved/dropped)" },
  { key: "biggest_wins", label: "Biggest wins" },
  { key: "biggest_drops", label: "Biggest drops" },
  { key: "ai_narrative", label: "AI narrative (estimated)" },
];

const CADENCES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
];

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const MINUTES = ["00", "15", "30", "45"];

// --- 12h <-> 24h helpers ---
function to24h(hour12, minute, ampm) {
  let h = parseInt(hour12, 10) % 12;
  if (ampm === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${minute}`;
}

function to12h(time24) {
  const [hStr, mStr] = (time24 || "08:00").split(":");
  const h = parseInt(hStr, 10);
  const m = mStr || "00";
  const ampm = h >= 12 ? "PM" : "AM";
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return { hour: String(h12), minute: m.padStart(2, "0"), ampm };
}

function formatTime12(time24) {
  const { hour, minute, ampm } = to12h(time24);
  return `${hour}:${minute} ${ampm}`;
}

// --- Time picker sub-component ---
function TimePicker12h({ value, onChange, testIdPrefix = "sched-time" }) {
  const { hour, minute, ampm } = to12h(value || "08:00");

  const update = (patch) => {
    const next = { hour, minute, ampm, ...patch };
    onChange(to24h(next.hour, next.minute, next.ampm));
  };

  return (
    <div className="flex items-center gap-1.5">
      <select
        data-testid={`${testIdPrefix}-hour`}
        value={hour}
        onChange={(e) => update({ hour: e.target.value })}
        className="px-2 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
      >
        {HOURS_12.map((h) => <option key={h} value={String(h)}>{h}</option>)}
      </select>
      <span className="text-[#8A94A6] text-sm">:</span>
      <select
        data-testid={`${testIdPrefix}-minute`}
        value={minute}
        onChange={(e) => update({ minute: e.target.value })}
        className="px-2 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
      >
        {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <select
        data-testid={`${testIdPrefix}-ampm`}
        value={ampm}
        onChange={(e) => update({ ampm: e.target.value })}
        className="px-2 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

export default function ReportsPage() {
  const [projects, setProjects] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    project_id: "",
    cadence: "weekly",
    send_time: "08:00",
    timezone_offset: (() => {
      // Guess a sensible default from the browser's current UTC offset so
      // users don't have to hunt through 40 entries on their first visit.
      const min = -new Date().getTimezoneOffset();
      const sign = min >= 0 ? "+" : "-";
      const abs = Math.abs(min);
      return `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
    })(),
    recipients_text: "",
    sections: ["executive_summary", "biggest_wins", "biggest_drops"],
    intro_text: "",
    include_ai_narrative: false,
  });
  const [editing, setEditing] = useState(null);
  const [tzOffsets, setTzOffsets] = useState([]);

  const load = () => api.get("/reports/schedules").then((r) => setSchedules(r.data));
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/reports/timezone-offsets").then((r) => setTzOffsets(r.data.offsets || [])).catch(() => setTzOffsets([]));
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const recipients = form.recipients_text.split(",").map((s) => s.trim()).filter(Boolean);
    if (!form.project_id || recipients.length === 0) return toast.error("Pick a project and at least one recipient.");
    try {
      await api.post("/reports/schedules", {
        project_id: form.project_id,
        cadence: form.cadence,
        send_time: form.send_time,
        timezone_offset: form.timezone_offset,
        recipients,
        sections: form.sections,
        intro_text: form.intro_text || null,
        include_ai_narrative: form.include_ai_narrative,
      });
      toast.success("Schedule created.");
      setShow(false);
      setForm({
        project_id: "",
        cadence: "weekly",
        send_time: "08:00",
        timezone_offset: form.timezone_offset,  // keep the user's choice for the next schedule
        recipients_text: "",
        sections: ["executive_summary", "biggest_wins", "biggest_drops"],
        intro_text: "",
        include_ai_narrative: false,
      });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/reports/schedules/${editing.id}`, {
        sections: editing.sections,
        intro_text: editing.intro_text,
        include_ai_narrative: editing.include_ai_narrative,
        cadence: editing.cadence,
        send_time: editing.send_time,
        timezone_offset: editing.timezone_offset || "+00:00",
      });
      toast.success("Schedule updated.");
      setEditing(null);
      load();
    } catch { toast.error("Update failed."); }
  };

  const toggleSection = (setter, current, key) => {
    if (current.includes(key)) setter(current.filter((s) => s !== key));
    else setter([...current, key]);
  };

  const sendNow = async (id) => {
    try {
      const r = await api.post(`/reports/schedules/${id}/send-now`);
      toast.success(`PDF report sent to ${r.data.recipients.join(", ")}`);
      load();
    } catch { toast.error("Send failed"); }
  };

  const toggle = async (s) => {
    await api.patch(`/reports/schedules/${s.id}`, { active: !s.active });
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete schedule?")) return;
    await api.delete(`/reports/schedules/${id}`);
    load();
  };

  const projectMap = Object.fromEntries(projects.map((p) => [p.id, p]));

  return (
    <div className="p-8 lg:p-10">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs text-[#8A94A6] uppercase tracking-wider font-medium">Scheduled reports</div>
          <h1 className="mt-2 text-3xl font-display font-bold text-[#0A0D14]">Reports delivered on schedule.</h1>
          <p className="mt-2 text-sm text-[#525B6A] max-w-xl">Daily, weekly, monthly or quarterly white-label PDF reports — delivered by email at the exact hour you choose.</p>
        </div>
        <button onClick={() => setShow(true)} data-testid="new-schedule-btn" className="btn-primary px-4 py-2.5 rounded-md text-sm inline-flex items-center gap-2"><Plus className="h-4 w-4" /> New schedule</button>
      </header>

      {show && (
        <form onSubmit={submit} className="mt-6 card-crisp p-6 grid md:grid-cols-6 gap-3 items-end" data-testid="new-schedule-form">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Project</label>
            <select required data-testid="sched-project" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
              <option value="">Select…</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Cadence</label>
            <select data-testid="sched-cadence" value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
              {CADENCES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-[#525B6A] mb-1 block inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Send time</label>
            <TimePicker12h value={form.send_time} onChange={(t) => setForm({ ...form, send_time: t })} testIdPrefix="sched-time" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Timezone</label>
            <select data-testid="sched-timezone" value={form.timezone_offset} onChange={(e) => setForm({ ...form, timezone_offset: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
              {tzOffsets.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-6">
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Recipients (comma-separated)</label>
            <input required data-testid="sched-recipients" placeholder="client@brand.com, seo@agency.com" value={form.recipients_text} onChange={(e) => setForm({ ...form, recipients_text: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </div>
          <div className="md:col-span-6 border-t border-[#E2E8F0] pt-3">
            <div className="text-xs text-[#525B6A] mb-2">Sections to include (in the PDF)</div>
            <div className="grid sm:grid-cols-2 gap-1.5">
              {SECTIONS.map((s) => (
                <label key={s.key} className="inline-flex items-center gap-2 text-xs text-[#0A0D14] cursor-pointer">
                  <input type="checkbox" data-testid={`section-${s.key}`} checked={form.sections.includes(s.key)} onChange={() => toggleSection((v) => setForm({ ...form, sections: v }), form.sections, s.key)} />
                  {s.label}
                </label>
              ))}
            </div>
            <label className="mt-3 inline-flex items-center gap-2 text-xs text-[#0A0D14] cursor-pointer">
              <input type="checkbox" data-testid="include-ai" checked={form.include_ai_narrative} onChange={(e) => setForm({ ...form, include_ai_narrative: e.target.checked })} />
              Generate an AI narrative each send (estimated · adds latency)
            </label>
          </div>
          <div className="md:col-span-6">
            <label className="text-xs font-medium text-[#525B6A] mb-1 block">Custom intro paragraph (optional — appears in the email cover)</label>
            <textarea data-testid="sched-intro" rows={2} placeholder="Hi team — here's this week's snapshot…" value={form.intro_text} onChange={(e) => setForm({ ...form, intro_text: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </div>
          <div className="md:col-span-6 flex gap-2">
            <button data-testid="sched-save" className="btn-primary px-4 py-2 rounded-md text-sm">Create schedule</button>
            <button type="button" onClick={() => setShow(false)} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
          </div>
        </form>
      )}

      {editing && (
        <div className="mt-6 card-crisp p-6 space-y-3" data-testid="edit-schedule">
          <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">Editing schedule</div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Cadence</label>
              <select data-testid="edit-cadence" value={editing.cadence} onChange={(e) => setEditing({ ...editing, cadence: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
                {CADENCES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Send time</label>
              <TimePicker12h value={editing.send_time} onChange={(t) => setEditing({ ...editing, send_time: t })} testIdPrefix="edit-time" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#525B6A] mb-1 block">Timezone</label>
              <select data-testid="edit-timezone" value={editing.timezone_offset || "+00:00"} onChange={(e) => setEditing({ ...editing, timezone_offset: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
                {tzOffsets.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-1.5">
            {SECTIONS.map((s) => (
              <label key={s.key} className="inline-flex items-center gap-2 text-xs text-[#0A0D14] cursor-pointer">
                <input type="checkbox" data-testid={`edit-section-${s.key}`} checked={editing.sections.includes(s.key)} onChange={() => toggleSection((v) => setEditing({ ...editing, sections: v }), editing.sections, s.key)} />
                {s.label}
              </label>
            ))}
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-[#0A0D14] cursor-pointer">
            <input type="checkbox" data-testid="edit-include-ai" checked={editing.include_ai_narrative} onChange={(e) => setEditing({ ...editing, include_ai_narrative: e.target.checked })} />
            Generate an AI narrative each send
          </label>
          <textarea data-testid="edit-intro" rows={2} placeholder="Intro text" value={editing.intro_text || ""} onChange={(e) => setEditing({ ...editing, intro_text: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          <div className="flex gap-2">
            <button onClick={saveEdit} data-testid="edit-sched-save" className="btn-primary px-4 py-2 rounded-md text-sm">Save changes</button>
            <button onClick={() => setEditing(null)} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-8 card-crisp overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 bg-[#F8F9FA] border-b border-[#E2E8F0] text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium">
          <div className="col-span-3">Project</div>
          <div className="col-span-2">Cadence</div>
          <div className="col-span-2">Send time</div>
          <div className="col-span-3">Recipients</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {schedules.length === 0 && (
          <div className="p-10 text-center text-[#8A94A6]">
            <CalendarCheck className="h-8 w-8 mx-auto mb-3 text-[#E2E8F0]" />
            No schedules yet. Create one above.
          </div>
        )}
        {schedules.map((s) => {
          const p = projectMap[s.project_id];
          return (
            <div key={s.id} data-testid={`schedule-${s.id}`} className="grid grid-cols-12 items-center px-4 py-3 border-b border-[#E2E8F0] text-sm">
              <div className="col-span-3">
                <div className="text-[#0A0D14] font-medium">{p?.name || "—"}</div>
                <div className="text-xs text-[#8A94A6]">{p?.domain}</div>
              </div>
              <div className="col-span-2 capitalize text-[#525B6A]">{s.cadence}</div>
              <div className="col-span-2 text-[#525B6A] text-xs">
                {formatTime12(s.send_time || "08:00")}
                <div className="text-[10px] text-[#8A94A6] mt-0.5">{s.timezone_offset === "+00:00" ? "UTC" : `GMT${s.timezone_offset || "+00:00"}`}</div>
              </div>
              <div className="col-span-3 text-[#525B6A] text-xs truncate" title={s.recipients.join(", ")}>{s.recipients.join(", ")}</div>
              <div className="col-span-1">
                <span className={`pill ${s.active ? 'pill-success' : ''}`}>{s.active ? "Active" : "Paused"}</span>
              </div>
              <div className="col-span-1 flex items-center justify-end gap-1">
                <button onClick={() => setEditing({ id: s.id, cadence: s.cadence, send_time: s.send_time || "08:00", sections: s.sections || ["executive_summary", "biggest_wins", "biggest_drops"], intro_text: s.intro_text || "", include_ai_narrative: !!s.include_ai_narrative })} data-testid={`edit-${s.id}`} title="Edit schedule" className="p-1.5 text-[#525B6A] hover:text-[#0047FF]"><CalendarCheck className="h-3.5 w-3.5" /></button>
                <button onClick={() => sendNow(s.id)} data-testid={`send-now-${s.id}`} title="Send PDF report now" className="p-1.5 text-[#525B6A] hover:text-[#0047FF]"><Send className="h-3.5 w-3.5" /></button>
                <button onClick={() => toggle(s)} data-testid={`toggle-${s.id}`} title={s.active ? "Pause" : "Resume"} className="p-1.5 text-[#525B6A] hover:text-[#0047FF]">{s.active ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}</button>
                <button onClick={() => del(s.id)} data-testid={`del-sched-${s.id}`} title="Delete" className="p-1.5 text-[#525B6A] hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              {s.last_run_at && <div className="col-span-12 text-xs text-[#8A94A6] mt-1">Last sent: {new Date(s.last_run_at).toLocaleString()} · Next: {new Date(s.next_run_at).toLocaleString()}</div>}
              {!s.last_run_at && s.next_run_at && <div className="col-span-12 text-xs text-[#8A94A6] mt-1">Next: {new Date(s.next_run_at).toLocaleString()}</div>}
            </div>
          );
        })}
      </div>

      <p className="mt-4 text-xs text-[#8A94A6] inline-flex items-center gap-1"><Mail className="h-3 w-3" /> Each scheduled send delivers the full white-label PDF as an email attachment.</p>
    </div>
  );
}
